/**
 * 確信度オーブ。0-1 の数値を high/medium/low に変換して円形で表示。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.3
 */
import { cn } from '@/lib/utils';

interface ConfidenceBadgeProps {
  /** 0-1 の数値 */
  confidence: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function tierFromScore(c: number): 'high' | 'medium' | 'low' {
  if (c >= 0.75) return 'high';
  if (c >= 0.4) return 'medium';
  return 'low';
}

const TIER_CLASS: Record<'high' | 'medium' | 'low', { bg: string; fg: string }> = {
  high: { bg: 'var(--accent-green-subtle)', fg: 'var(--accent-green)' },
  medium: { bg: 'var(--accent-amber-subtle)', fg: 'var(--accent-amber)' },
  low: { bg: 'var(--accent-red-subtle)', fg: 'var(--accent-red)' },
};

const SIZE_CLASS: Record<'sm' | 'md' | 'lg', { wh: string; text: string; label: string }> = {
  sm: { wh: 'size-9', text: 'text-[12px]', label: 'text-[9px]' },
  md: { wh: 'size-12', text: 'text-[15px]', label: 'text-[10px]' },
  lg: { wh: 'size-14', text: 'text-[18px]', label: 'text-[10px]' },
};

export function ConfidenceBadge({
  confidence,
  size = 'md',
  className,
}: ConfidenceBadgeProps) {
  const tier = tierFromScore(confidence);
  const { bg, fg } = TIER_CLASS[tier];
  const sz = SIZE_CLASS[size];
  const score = Math.round(confidence * 100);
  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      <div
        className={cn(
          sz.wh,
          'rounded-full flex items-center justify-center font-semibold tabular-nums',
          sz.text,
        )}
        style={{ background: bg, color: fg }}
      >
        {score}
      </div>
      <span
        className={cn(
          sz.label,
          'uppercase tracking-wider text-[var(--text-muted)]',
        )}
      >
        {tier}
      </span>
    </div>
  );
}
